import React, { useState } from "react";
import {
  EyeOff, Microscope, Users, KeyRound, Workflow, Crosshair, Repeat,
  CheckCircle2, FileSearch, Presentation, Send,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { Eyebrow, BookButton } from "@/components/Bits";
import { IMAGES } from "@/lib/site";

const external = [
  { t: "No Target Access Required", b: "We do not require access to the business owner, management team, or internal data rooms." },
  { t: "Zero Footprint Execution", b: "We analyze public signals, market footprint, broker CIMs/teasers, and organizational artifacts — without alerting the market or the seller." },
  { t: "Deal Leverage", b: "Identify founder dependency and walk-away flaws early so you can adjust valuation parameters or write precise diligence conditions into your initial offer." },
];

const internal = [
  { t: "Cultural Alignment Scan", b: "With data room and management access, we evaluate real-world operational health, communication blockages, and institutional power centers." },
  { t: "Ownership Transition Blueprint", b: "We audit the compatibility between your incoming team and remaining employees to ensure immediate stability and avoid post-acquisition talent drain." },
  { t: "Integration Readiness", b: "A clear view of which legacy systems and habits will resist integration — before they erode the value you paid for." },
];

const fiveP = [
  { key: "People", icon: Users, b: "Uncover unmapped key-person reliance in sales, operations, or technical execution — the individuals the business cannot run without." },
  { key: "Power", icon: KeyRound, b: "Isolate where key approvals and critical relationships are locked strictly inside the founder’s hands." },
  { key: "Process", icon: Workflow, b: "Grade the target’s operational maturity and its ability to scale without informal, verbal direction." },
  { key: "Positioning", icon: Crosshair, b: "Pinpoint gaps between the public growth narrative and actual internal operational capacity." },
  { key: "Path-Dependency", icon: Repeat, b: "Identify legacy cultural habits or operational systems that will resist post-close integration." },
];

const pipeline = [
  { t: "Target Submission", icon: Send, b: "Secure, conflict-checked onboarding to protect your transaction pipeline." },
  { t: "Independent / Collaborative Review", icon: FileSearch, b: "We map target signals across the 5P Framework — stealth or with deep access." },
  { t: "Risk Assessment Report & Presentation", icon: Presentation, b: "An actionable, executive-level brief with prioritized diligence levers." },
];

const BuyersPage = () => {
  const [active, setActive] = useState(0);
  return (
    <main data-testid="buyers-page" className="pt-20">
      {/* HERO */}
      <section className="bg-cream py-20 md:py-28 relative">
        <div className="grid-lines" />
        <div className="max-w-7xl mx-auto px-5 md:px-10 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <Eyebrow>For Corporate Buyers, Searchers &amp; Family Offices</Eyebrow>
              <h1 className="font-serif text-4xl md:text-[3.6rem] leading-[1.08] text-ink-deep">
                De-risk your <span className="italic text-brand">acquisition.</span>
              </h1>
              <p className="mt-7 text-lg text-[#475569]">
                Traditional buy-side diligence looks backward. Financial reviews verify past performance; legal
                reviews check historical compliance — but neither tells you whether the target’s value will evaporate
                the moment the founder walks out the door.
              </p>
              <p className="mt-4 text-base text-[#64748B]">
                We provide Business Transition Intelligence to identify hidden operational dependencies, key-person
                risks, and cultural fragility before they become post-close liabilities.
              </p>
              <div className="mt-9">
                <BookButton label="Initiate a Confidential Target Scan" variant="dark" testId="buyers-hero-cta" />
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] overflow-hidden border border-[#E2E8F0] bg-[#EEF2F6]">
                <img src={IMAGES.geo1} alt="Geometric structure" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TWO PHASES SIDE-BY-SIDE */}
      <section data-testid="phases-section" className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <Eyebrow>Two Phases of Your Pipeline</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep">From stealth signal-reading to deep-access compatibility.</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {/* External */}
            <Reveal className="flex flex-col border border-[#E2E8F0] bg-cream p-9">
              <div className="flex items-center gap-3 mb-2">
                <EyeOff className="w-7 h-7 text-brand" />
                <span className="text-xs uppercase tracking-[0.2em] text-brand font-semibold">01 · Pre-LOI · Complete Stealth</span>
              </div>
              <h3 className="font-serif text-2xl text-ink-deep mb-5">External Analysis: Outside-In Risk Read</h3>
              <div className="space-y-5">
                {external.map((e, i) => (
                  <div key={i} className="border-l-2 border-brand/30 pl-5">
                    <p className="font-semibold text-ink mb-1">{e.t}</p>
                    <p className="text-[#475569] text-sm">{e.b}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            {/* Internal */}
            <Reveal delay={120} className="flex flex-col bg-ink-deep p-9">
              <div className="flex items-center gap-3 mb-2">
                <Microscope className="w-7 h-7 text-brand-light" />
                <span className="text-xs uppercase tracking-[0.2em] text-brand-light font-semibold">02 · Post-LOI · Deep Access</span>
              </div>
              <h3 className="font-serif text-2xl text-white mb-5">Internal Analysis: Inside-Out Compatibility Audit</h3>
              <div className="space-y-5">
                {internal.map((e, i) => (
                  <div key={i} className="border-l-2 border-brand/50 pl-5">
                    <p className="font-semibold text-white mb-1">{e.t}</p>
                    <p className="text-[#CBD5E1] text-sm">{e.b}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 5P FRAMEWORK — INTERACTIVE TABS */}
      <section data-testid="fivep-section" className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-12">
            <Eyebrow>The Deliverable · Acquisition Risk Brief</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep">The 5P Framework.</h2>
            <p className="mt-4 text-[#475569]">
              We map target signals across five proprietary dimensions to deliver an actionable, executive-level
              risk assessment.
            </p>
          </div>
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Tabs */}
            <div className="lg:col-span-4 flex lg:flex-col gap-2 overflow-x-auto">
              {fiveP.map((p, i) => (
                <button
                  key={i}
                  data-testid={`fivep-tab-${i}`}
                  onClick={() => setActive(i)}
                  className={`flex items-center gap-3 px-5 py-4 text-left border transition-colors whitespace-nowrap lg:whitespace-normal flex-1 lg:flex-none ${
                    active === i
                      ? "bg-ink-deep border-ink-deep text-white"
                      : "bg-white border-[#E2E8F0] text-ink hover:border-brand"
                  }`}
                >
                  <p.icon className={`w-5 h-5 ${active === i ? "text-brand-light" : "text-brand"}`} />
                  <span className="font-serif text-lg">{p.key}</span>
                </button>
              ))}
            </div>
            {/* Panel */}
            <div className="lg:col-span-8">
              <div data-testid="fivep-panel" className="h-full bg-white border border-[#E2E8F0] p-9 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-4 mb-6">
                  {React.createElement(fiveP[active].icon, { className: "w-10 h-10 text-brand" })}
                  <h3 className="font-serif text-3xl text-ink-deep">{fiveP[active].key}</h3>
                </div>
                <p className="text-lg text-[#475569]">{fiveP[active].b}</p>
              </div>
            </div>
          </div>
          {/* Outcome */}
          <Reveal className="mt-8 bg-ink-deep p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6">
            <CheckCircle2 className="w-10 h-10 text-brand-light shrink-0" />
            <p className="text-[#CBD5E1] text-lg">
              <span className="text-white font-semibold">The Outcome:</span> a targeted checklist of high-priority
              diligence questions and customized LOI structural levers to safeguard your deployed capital.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PIPELINE FIT */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <Eyebrow>How We Fit Into Your Pipeline</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep">A clean, conflict-checked workflow.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {pipeline.map((p, i) => (
              <Reveal key={i} delay={i * 110} className="relative border border-[#E2E8F0] bg-cream p-8">
                <span className="font-serif text-5xl text-[#E2E8F0] absolute top-5 right-6">0{i + 1}</span>
                <p.icon className="w-8 h-8 text-brand mb-6" />
                <h3 className="font-serif text-xl text-ink-deep mb-3">{p.t}</h3>
                <p className="text-[#475569] text-sm">{p.b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-ink-deep py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-5xl text-white leading-tight">
            Know exactly what you are purchasing.
          </h2>
          <p className="mt-6 text-lg text-[#94A3B8]">
            Secure, conflict-checked onboarding to protect your transaction pipeline.
          </p>
          <div className="mt-9 flex justify-center">
            <BookButton label="Initiate a Confidential Target Scan" variant="light" testId="buyers-final-cta" />
          </div>
        </div>
      </section>
    </main>
  );
};

export default BuyersPage;
